import React from 'react';

const positive = (value: unknown): number | null => {
  const numeric = Number(value);
  return value !== null && value !== undefined && Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const formatMm = (value: number) => `${value.toLocaleString('pl-PL', { maximumFractionDigits: 0 })} mm`;

interface PieceDrawingProps {
  length?: number | null;
  width?: number | null;
  thickness?: number | null;
  label: string;
  lengthLabel: string;
  widthLabel: string;
}

/** Isometric sketch of one piece; missing sides are labelled with "?". */
export const PieceDrawing: React.FC<PieceDrawingProps> = ({
  length, width, thickness, label, lengthLabel, widthLabel,
}) => {
  const L = positive(length);
  const W = positive(width);
  const T = positive(thickness);
  const rl = L ?? 2000;
  const rw = W ?? 1000;
  const scale = 190 / Math.max(rl, rw * 1.4);
  const w = rl * scale;
  const h = rw * scale * 0.5;
  const d = Math.max(6, Math.min(18, (T ?? 20) * scale * 2));
  const x0 = Math.max(46, (300 - w - h * 0.6) / 2 - 10);
  const y0 = 30;
  const top = `${x0 + h * 0.6},${y0} ${x0 + h * 0.6 + w},${y0} ${x0 + w},${y0 + h} ${x0},${y0 + h}`;
  return (
    <svg className="product-card-drawing" viewBox={`0 0 300 ${y0 + h + d + 40}`} role="img" aria-label={label}>
      <polygon points={top} className="product-card-drawing__top" />
      <polygon points={`${x0},${y0 + h} ${x0 + w},${y0 + h} ${x0 + w},${y0 + h + d} ${x0},${y0 + h + d}`} className="product-card-drawing__front" />
      <polygon points={`${x0 + w},${y0 + h} ${x0 + h * 0.6 + w},${y0} ${x0 + h * 0.6 + w},${y0 + d} ${x0 + w},${y0 + h + d}`} className="product-card-drawing__side" />
      <text x={x0 + w / 2} y={y0 + h + d + 18} textAnchor="middle" className={L ? 'product-card-drawing__label' : 'product-card-drawing__label is-missing'}>
        {lengthLabel} {L ? formatMm(L) : '?'}
      </text>
      <text x={x0 + w + h * 0.3 + 8} y={y0 + h / 2} textAnchor="start" transform={`rotate(-40 ${x0 + w + h * 0.3 + 8} ${y0 + h / 2})`} className={W ? 'product-card-drawing__label' : 'product-card-drawing__label is-missing'}>
        {widthLabel} {W ? formatMm(W) : '?'}
      </text>
      <text x={x0 - 6} y={y0 + h + d / 2 + 4} textAnchor="end" className={T ? 'product-card-drawing__label' : 'product-card-drawing__label is-missing'}>
        {T ? formatMm(T) : '?'}
      </text>
    </svg>
  );
};

interface PalletDrawingProps {
  lengthMm?: number | null;
  widthMm?: number | null;
  filled: boolean;
  layers: number;
  label: string;
}

/** Schematic loaded pallet; purely illustrative (layer count is capped). */
export const PalletDrawing: React.FC<PalletDrawingProps> = ({ lengthMm, widthMm, filled, layers, label }) => {
  const L = positive(lengthMm) ?? 1200;
  const W = positive(widthMm) ?? 800;
  const scale = 250 / Math.max(L, W * 1.6);
  const w = L * scale;
  const d = W * scale * 0.45;
  const count = Math.max(1, Math.min(8, layers));
  const lh = Math.max(6, 70 / count);
  const x0 = (280 - w - d) / 2 + d;
  const y0 = 110;
  return (
    <svg className="product-card-drawing product-card-drawing--pallet" viewBox="0 0 280 140" role="img" aria-label={label}>
      <polygon points={`${x0},${y0} ${x0 + w},${y0} ${x0 + w - d},${y0 + d * 0.5} ${x0 - d},${y0 + d * 0.5}`} className="product-card-drawing__deck" />
      <rect x={x0 - d} y={y0 + d * 0.5} width={w} height={8} className="product-card-drawing__base" />
      {Array.from({ length: count }, (_, index) => {
        const y = y0 - (index + 1) * lh;
        return (
          <g key={index} className={filled ? 'product-card-drawing__load' : 'product-card-drawing__load is-empty'}>
            <polygon points={`${x0},${y} ${x0 + w},${y} ${x0 + w - d},${y + d * 0.5} ${x0 - d},${y + d * 0.5}`} className={index % 2 ? 'is-dark' : 'is-light'} />
            <rect x={x0 - d} y={y + d * 0.5} width={w} height={lh} className="is-front" />
            <polygon points={`${x0 + w - d},${y + d * 0.5} ${x0 + w},${y} ${x0 + w},${y + lh} ${x0 + w - d},${y + d * 0.5 + lh}`} className="is-side" />
          </g>
        );
      })}
      <text x={x0 - d + w / 2} y={136} textAnchor="middle" className="product-card-drawing__caption">
        {`${L.toLocaleString('pl-PL')} × ${W.toLocaleString('pl-PL')} mm`}
      </text>
    </svg>
  );
};
